import React, { FC, Suspense, useState } from 'react'
import { Button, Loading, TitleController } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { Alert } from '@pandiafi/components'

import { MessageTimeline } from './MessageTimeline'
import { MessageFormContainer } from './MessageForm'
import { MessagesViewDataLoader } from './MessagesViewDataLoader'

import {
  ResidentNotifications,
  SidebarInfoSection,
  SidebarInfoSectionText,
  View,
} from 'components'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import { NotFound } from 'views/NotFound'
import { useModuleConfig } from 'hooks/useModuleConfig/useModuleConfig'
import { useAPIGetUserMetadataSuspense } from 'api/apiHooks/useAPIGetUserMetadata'

export const Messages: FC = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['common', 'messages', 'titles'])
  const { frontPagePath, messageThreadPath } = useContractPaths()
  const { messaging, extendedMessaging } = useModuleConfig()
  const { data: userMetadata } = useAPIGetUserMetadataSuspense()

  const navi = useNavigate()

  const [showForm, setShowForm] = useState(false)
  const { messageThreadUuid } = useParams() as { messageThreadUuid?: string }

  const [formState, setFormState] = useState<{
    messageThreadUuid?: string
    replySubject?: string
    focusSubject: boolean
  }>({ focusSubject: false, messageThreadUuid })

  const handleCreateNewMessage = () => {
    setFormState({
      messageThreadUuid: undefined,
      replySubject: undefined,
      focusSubject: true,
    })
    setShowForm(true)
  }

  const handleToggleForm = () => {
    if (showForm) {
      setShowForm(false)
      setFormState({ focusSubject: false })
    } else {
      handleCreateNewMessage()
    }
  }

  if (!messaging) {
    return <NotFound />
  }

  return (
    <TitleController title={t('titles:messages')}>
      <View>
        <View.Header>
          {showForm ? (
            <>
              <View.HeaderButton
                onClick={handleToggleForm}
                ariaLabel={t('messages:view.title')}
              >
                {t('messages:view.title')}
              </View.HeaderButton>
              <View.HeaderTitle>
                {t('messages:view.sendNewMessage')}
              </View.HeaderTitle>
            </>
          ) : (
            <>
              <View.HeaderLink to={frontPagePath}>
                {t('common:views.home')}
              </View.HeaderLink>
              <View.HeaderTitle>{t('messages:view.title')}</View.HeaderTitle>
            </>
          )}
        </View.Header>

        <View.Sidebar>
          <SidebarInfoSection>
            <SidebarInfoSectionText>
              {t('messages:view.description')}
            </SidebarInfoSectionText>
          </SidebarInfoSection>
        </View.Sidebar>

        <View.Content>
          <>
            {/* Hidden notifications component to handle closing message notifications */}
            <div className="hidden">
              <ResidentNotifications />
            </div>

            {extendedMessaging && (
              <>
                {showForm ? (
                  <MessageFormContainer
                    key={formState.messageThreadUuid ?? 'new'}
                    threadId={formState.messageThreadUuid}
                    replySubject={formState.replySubject}
                    focusSubject={formState.focusSubject}
                    showParticipantsNotice={false}
                    onCancel={() => setShowForm(false)}
                    onSuccess={() => {
                      queryClient.invalidateQueries({
                        queryKey: ['resident-message-timeline'],
                      })
                    }}
                  />
                ) : (
                  <div className="flex justify-end mb-5">
                    <Button
                      onClick={handleToggleForm}
                      disabled={userMetadata.isImpersonator}
                    >
                      {t('messages:view.sendNewMessage')}
                    </Button>
                  </div>
                )}
              </>
            )}

            {!showForm && (
              <Suspense fallback={<Loading variant="details" />}>
                <MessagesViewDataLoader>
                  {(messages) =>
                    messages.length === 0 ? (
                      <Alert
                        variant="info"
                        className="my-8"
                        data-testid="empty-message-alert"
                      >
                        {t('messages:view.empty')}
                      </Alert>
                    ) : (
                      <MessageTimeline
                        messages={messages}
                        onViewThread={(id: string) => {
                          if (userMetadata.isImpersonator) return
                          setFormState({
                            messageThreadUuid: id,
                            focusSubject: false,
                          })

                          navi(
                            messageThreadPath.replace(':messageThreadUuid', id)
                          )
                        }}
                      />
                    )
                  }
                </MessagesViewDataLoader>
              </Suspense>
            )}
          </>
        </View.Content>
      </View>
    </TitleController>
  )
}
